import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { api, CITIES } from "@/lib/api";
import Seo from "@/components/Seo";
import { Phone, Globe, Mail, MapPin, Search, X, BadgeCheck } from "lucide-react";

export default function ClinicsPage() {
    const [clinics, setClinics] = useState([]);
    const [filter, setFilter] = useState("All");
    const [query, setQuery] = useState("");
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get("/clinics").then((r) => setClinics(r.data)).finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        return clinics.filter((c) => {
            const cityMatch = filter === "All" || (c.city && c.city.toLowerCase() === filter.toLowerCase());
            const q = query.trim().toLowerCase();
            const qMatch = !q || c.name?.toLowerCase().includes(q) || c.address?.toLowerCase().includes(q);
            return cityMatch && qMatch;
        });
    }, [clinics, filter, query]);

    const mapsEmbed = (address) =>
        `https://www.google.com/maps?q=${encodeURIComponent(address || "New Zealand")}&output=embed`;

    return (
        <div className="page-fade pt-32 md:pt-40">
            <Seo
                title="Aqualyx Certified Clinics in New Zealand — Find Your Provider"
                description="Find your nearest Aqualyx Certified Clinic in New Zealand. 36+ verified providers across Auckland, Wellington, Christchurch, Queenstown, Hamilton, Tauranga, Palmerston North, Dunedin and more. Original Aqualyx fat reduction."
                path="/clinics"
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: "Certified Clinics", path: "/clinics" },
                ]}
                schema={{
                    "@context": "https://schema.org",
                    "@type": "ItemList",
                    "name": "Aqualyx Certified Clinics in New Zealand",
                    "numberOfItems": clinics.length,
                    "itemListElement": clinics.slice(0, 50).map((c, i) => ({
                        "@type": "ListItem",
                        "position": i + 1,
                        "item": {
                            "@type": "MedicalClinic",
                            "name": c.name,
                            "address": c.address,
                            "telephone": c.phone,
                            "email": c.email,
                            "url": c.website,
                            "areaServed": c.city || "New Zealand",
                            "medicalSpecialty": "AestheticMedicine"
                        }
                    }))
                }}
            />
            <Helmet>
                <meta name="keywords" content="Aqualyx certified clinic NZ, Aqualyx Auckland, Aqualyx Wellington, Aqualyx Christchurch, Aqualyx Queenstown, fat dissolving clinic New Zealand, find Aqualyx provider" />
            </Helmet>

            <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-16">
                    <div className="lg:col-span-8 space-y-6">
                        <div className="eyebrow">Clinic Locator</div>
                        <h1 className="editorial-headline text-6xl md:text-8xl">
                            Aqualyx<br />Certified<br /><span className="italic text-champagne">Clinics.</span>
                        </h1>
                    </div>
                    <div className="lg:col-span-4 space-y-4">
                        <p className="text-ink/70 leading-relaxed">
                            Only certified clinics offer the original Aqualyx in New Zealand. Use the search and city filter to find your nearest provider.
                        </p>
                        <div className="inline-flex items-center gap-2 border border-champagne/40 px-4 py-2">
                            <BadgeCheck size={16} strokeWidth={1.2} className="text-champagne" />
                            <span className="eyebrow">Official NZ Provider</span>
                        </div>
                    </div>
                </div>

                <div data-testid="clinic-filter-bar" className="flex flex-col md:flex-row gap-6 items-stretch md:items-center border-t border-b border-champagne/30 py-6 mb-12">
                    <div className="flex items-center gap-3 flex-1 border border-ink/20 px-4 py-3">
                        <Search size={16} strokeWidth={1.2} />
                        <input
                            data-testid="clinic-search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search by clinic name or area"
                            className="bg-transparent flex-1 focus:outline-none text-sm"
                        />
                        {query && (
                            <button onClick={() => setQuery("")} aria-label="Clear search"><X size={14} /></button>
                        )}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        {["All", ...CITIES].map((c) => (
                            <button
                                key={c}
                                onClick={() => setFilter(c)}
                                data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                                className={`text-[0.65rem] uppercase tracking-[0.2em] px-4 py-2 border transition-all ${
                                    filter === c
                                        ? "bg-ink text-bone border-ink"
                                        : "border-champagne/50 text-ink hover:bg-nude"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-20 eyebrow">Loading clinics…</div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-20">
                        <p className="font-serif text-3xl italic">No clinics match your search.</p>
                        <p className="text-ink/60 mt-2">Try a different city or search term.</p>
                    </div>
                ) : (
                    <div data-testid="clinic-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-champagne/30 border border-champagne/30">
                        {filtered.map((c, i) => (
                            <article key={c.id} data-testid={`clinic-card-${i}`} className="bg-bone p-8 space-y-5 flex flex-col">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="eyebrow text-champagne">Certified · {c.city || "NZ"}</div>
                                    <BadgeCheck size={18} strokeWidth={1.2} className="text-champagne shrink-0" />
                                </div>
                                <h2 className="font-serif text-2xl md:text-3xl leading-tight">{c.name}</h2>
                                {c.address && (
                                    <p className="text-sm text-ink/60 leading-relaxed flex items-start gap-2">
                                        <MapPin size={14} strokeWidth={1.2} className="mt-1 shrink-0" />
                                        {c.address}
                                    </p>
                                )}
                                <div className="space-y-2 text-xs text-ink/70 pt-2 border-t border-champagne/20">
                                    {c.phone && (
                                        <a href={`tel:${c.phone.replace(/\s+/g, "")}`} className="flex items-center gap-2 hover:text-champagne" data-testid={`clinic-phone-${i}`}>
                                            <Phone size={12} strokeWidth={1.2} /> {c.phone}
                                        </a>
                                    )}
                                    {c.email && (
                                        <a href={`mailto:${c.email}`} className="flex items-center gap-2 hover:text-champagne break-all">
                                            <Mail size={12} strokeWidth={1.2} /> {c.email}
                                        </a>
                                    )}
                                    {c.website && (
                                        <a href={c.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-champagne">
                                            <Globe size={12} strokeWidth={1.2} /> Visit website
                                        </a>
                                    )}
                                </div>
                                <div className="flex gap-2 mt-auto pt-4">
                                    <button
                                        onClick={() => setSelected(c)}
                                        data-testid={`view-map-${i}`}
                                        className="btn-outline text-[0.6rem] flex-1"
                                    >
                                        View Map
                                    </button>
                                    <Link
                                        to={`/contact?clinic=${encodeURIComponent(c.name)}`}
                                        data-testid={`book-consult-${i}`}
                                        className="btn-primary text-[0.6rem] flex-1"
                                    >
                                        Book Consult
                                    </Link>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </section>

            {selected && (
                <div
                    data-testid="map-modal"
                    onClick={() => setSelected(null)}
                    className="fixed inset-0 z-[60] bg-ink/80 backdrop-blur-sm flex items-center justify-center p-4"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="bg-bone w-full max-w-4xl max-h-[90vh] overflow-auto"
                    >
                        <div className="flex items-center justify-between p-6 border-b border-champagne/30">
                            <div>
                                <div className="eyebrow text-champagne">Certified Clinic</div>
                                <h3 className="font-serif text-3xl">{selected.name}</h3>
                            </div>
                            <button onClick={() => setSelected(null)} data-testid="close-map" aria-label="Close">
                                <X strokeWidth={1.2} />
                            </button>
                        </div>
                        <div className="aspect-video">
                            <iframe
                                title={selected.name}
                                src={mapsEmbed(selected.address)}
                                className="w-full h-full border-0"
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>
                        <div className="p-6 space-y-3 text-sm">
                            <p className="text-ink/70">{selected.address}</p>
                            <div className="flex gap-3 flex-wrap">
                                {selected.phone && <a href={`tel:${selected.phone.replace(/\s+/g,"")}`} className="btn-outline text-[0.6rem]"><Phone size={12}/> Call</a>}
                                {selected.website && <a href={selected.website} target="_blank" rel="noreferrer" className="btn-outline text-[0.6rem]"><Globe size={12}/> Website</a>}
                                <Link to={`/contact?clinic=${encodeURIComponent(selected.name)}`} className="btn-primary text-[0.6rem]">Book Consultation</Link>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
